import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../contexts/AuthContext';
import {
  FaEdit,
  FaTrash,
  FaShoppingCart,
  FaTimes,
  FaPlus,
  FaBoxOpen,
  FaChevronLeft,
  FaChevronRight,
  FaUpload,
  FaStar,
  FaLock,
  FaExternalLinkAlt,
} from 'react-icons/fa';
import { db } from '../firebase';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  where,
  getDoc,
  orderBy,
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import '../styles/Store.css';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';

// Update ProductCard to only use props for handling clicks
const ProductCard = ({
  product,
  onImageClick,
  onAddToCartClick,
  isAdmin,
  onEdit,
  onDelete,
}) => {
  const { currentUser } = useAuth();
  const [showDetails, setShowDetails] = useState(false);

  // Format description with proper truncation
  const formatDescription = (text) => {
    if (!text) return '';
    const maxLength = 100;
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  // Get button text based on product status
  const getButtonText = () => {
    if (!currentUser) {
      return (
        <Link to="/login" className="btn-login-to-purchase">
          <FaLock /> Sign in to Purchase
        </Link>
      );
    }

    if (product.isOutOfStock) return 'Out of Stock';
    return 'View product';
  };

  const handleCardClick = () => {
    setShowDetails(true);
  };

  const handleActionClick = (e) => {
    e.stopPropagation();
    if (!product.isOutOfStock) {
      setShowDetails(true);
    }
  };

  return (
    <>
      <div
        className={`product-card ${product.isOutOfStock ? 'out-of-stock' : ''}`}
        onClick={handleCardClick}
      >
        <div className="product-images">
          <img
            src={product.images?.[0] || '/placeholder-image.jpg'}
            alt={product.name}
          />
          {product.images?.length > 1 && (
            <div className="image-dots">
              {product.images.map((_, index) => (
                <span key={index} className="dot" />
              ))}
            </div>
          )}
        </div>

        <div className="product-card-content">
          <h3>{product.name}</h3>
          <p className="product-description">
            {formatDescription(product.description)}
          </p>
          <div className="price">
            <span>LKR {product.price}</span>
            {product.isMembersOnly && !currentUser && (
              <span className="members-tag">Members Only</span>
            )}
          </div>
        </div>

        <div className="product-card-footer">
          {isAdmin && (
            <div className="admin-actions">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
              >
                <FaEdit />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
              >
                <FaTrash />
              </button>
            </div>
          )}
          {currentUser ? (
            <button
              className={`add-to-cart-btn ${
                product.isMembersOnly && !currentUser ? 'members-only' : ''
              }`}
              onClick={handleActionClick}
              disabled={product.isOutOfStock}
            >
              {getButtonText()}
            </button>
          ) : (
            <Link to="/login" className="btn-login-to-purchase">
              <FaLock /> Sign in to Purchase
            </Link>
          )}
        </div>
      </div>

      {showDetails && (
        <ProductDetailsModal
          product={product}
          onClose={() => setShowDetails(false)}
          onAddToCart={onAddToCartClick}
        />
      )}
    </>
  );
};

// Update ProductCard PropTypes
ProductCard.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    images: PropTypes.arrayOf(PropTypes.string),
    variants: PropTypes.shape({
      colors: PropTypes.arrayOf(PropTypes.string),
      sizes: PropTypes.arrayOf(PropTypes.string),
    }),
    isMembersOnly: PropTypes.bool,
    isOutOfStock: PropTypes.bool,
  }).isRequired,
  onImageClick: PropTypes.func.isRequired,
  onAddToCartClick: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
};

// Move modals outside main component
const ProductModal = ({
  onClose,
  onSubmit,
  productForm,
  setProductForm,
  selectedProduct,
}) => {
  const handleImageChange = (index, value) => {
    const newImages = [...productForm.images];
    newImages[index] = value;
    setProductForm({ ...productForm, images: newImages });
  };

  const removeImageField = (index) => {
    const newImages = productForm.images.filter((_, i) => i !== index);
    setProductForm({ ...productForm, images: newImages });
  };

  // Remove duplicate handleColorChange function and consolidate color handling
  const handleColorChange = (color) => {
    if (!color) return;

    // Prevent duplicates
    if (!productForm.variants.colors.includes(color)) {
      setProductForm({
        ...productForm,
        variants: {
          ...productForm.variants,
          colors: [...productForm.variants.colors, color],
        },
      });
    }
  };

  const removeColor = (colorToRemove) => {
    setProductForm({
      ...productForm,
      variants: {
        ...productForm.variants,
        colors: productForm.variants.colors.filter(
          (color) => color !== colorToRemove,
        ),
      },
    });
  };

  const handleImageUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const storage = getStorage();
      const storageRef = ref(
        storage,
        `product-images/${Date.now()}-${file.name}`,
      );

      // Show loading state
      const newImages = [...productForm.images];
      newImages[index] = 'uploading...';
      setProductForm({ ...productForm, images: newImages });

      // Upload file
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      // Update form with new image URL
      newImages[index] = downloadURL;
      setProductForm({ ...productForm, images: newImages });
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');

      // Reset the uploading state
      const newImages = [...productForm.images];
      newImages[index] = '';
      setProductForm({ ...productForm, images: newImages });
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <button className="close-button" onClick={onClose}>
            <FaTimes />
          </button>
          <h2>{selectedProduct ? 'Edit Product' : 'Add Product'}</h2>
          <form onSubmit={onSubmit}>
            <input
              type="text"
              placeholder="Product Name"
              value={productForm.name}
              onChange={(e) =>
                setProductForm({ ...productForm, name: e.target.value })
              }
              required
            />

            <select
              value={productForm.category}
              onChange={(e) =>
                setProductForm({ ...productForm, category: e.target.value })
              }
              required
            >
              <option value="">Select Category</option>
              <option value="tshirt">T-Shirt</option>
              <option value="mug">Mug</option>
              <option value="cap">Cap</option>
              <option value="keytag">Key Tag</option>
              <option value="other">Other</option>
            </select>

            {productForm.category === 'tshirt' && (
              <div className="size-options">
                <label>Available Sizes:</label>
                <div className="checkbox-group">
                  {['S', 'M', 'L', 'XL', 'XXL'].map((size) => (
                    <label key={size}>
                      <input
                        type="checkbox"
                        checked={productForm.sizes?.includes(size) || false}
                        onChange={(e) => {
                          const newSizes = e.target.checked
                            ? [...(productForm.sizes || []), size]
                            : (productForm.sizes || []).filter(
                                (s) => s !== size,
                              );
                          setProductForm({ ...productForm, sizes: newSizes });
                        }}
                      />
                      {size}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Multiple Images */}
            <div className="images-section">
              <label>Product Images</label>
              {productForm.images.map((url, index) => (
                <div key={index} className="image-input-group">
                  <div className="image-upload-wrapper">
                    <input
                      type="url"
                      placeholder="Image URL"
                      value={url === 'uploading...' ? '' : url}
                      onChange={(e) => handleImageChange(index, e.target.value)}
                      disabled={url === 'uploading...'}
                    />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, index)}
                      id={`image-upload-${index}`}
                      className="file-input"
                    />
                    <label
                      htmlFor={`image-upload-${index}`}
                      className="upload-btn"
                    >
                      <FaUpload /> Upload
                    </label>
                  </div>
                  {url && url !== 'uploading...' && (
                    <div className="image-preview">
                      <img src={url} alt={`Preview ${index + 1}`} />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => removeImageField(index)}
                    className="remove-image"
                    disabled={url === 'uploading...'}
                  >
                    <FaTimes />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() =>
                  setProductForm({
                    ...productForm,
                    images: [...productForm.images, ''],
                  })
                }
                className="add-image"
              >
                <FaPlus /> Add Image
              </button>
            </div>

            {/* Colors */}
            <div className="colors-section">
              <label>Available Colors</label>
              <div className="color-inputs">
                <input
                  type="text"
                  placeholder="Add a color (e.g., Black)"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleColorChange(e.target.value);
                      e.target.value = '';
                    }
                  }}
                />
                <div className="color-tags">
                  {productForm.variants.colors.map((color) => (
                    <span key={color} className="color-tag">
                      {color}
                      <button type="button" onClick={() => removeColor(color)}>
                        <FaTimes />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Category-specific details */}
            {productForm.category === 'tshirt' && (
              <div className="product-details">
                <input
                  type="text"
                  placeholder="Material (e.g., 100% Cotton)"
                  value={productForm.details.tshirt.material}
                  onChange={(e) =>
                    setProductForm({
                      ...productForm,
                      details: {
                        ...productForm.details,
                        tshirt: {
                          ...productForm.details.tshirt,
                          material: e.target.value,
                        },
                      },
                    })
                  }
                />
                <input
                  type="text"
                  placeholder="Weight (e.g., 180gsm)"
                  value={productForm.details.tshirt.weight}
                  onChange={(e) =>
                    setProductForm({
                      ...productForm,
                      details: {
                        ...productForm.details,
                        tshirt: {
                          ...productForm.details.tshirt,
                          weight: e.target.value,
                        },
                      },
                    })
                  }
                />
                <select
                  value={productForm.details.tshirt.fit}
                  onChange={(e) =>
                    setProductForm({
                      ...productForm,
                      details: {
                        ...productForm.details,
                        tshirt: {
                          ...productForm.details.tshirt,
                          fit: e.target.value,
                        },
                      },
                    })
                  }
                >
                  <option value="">Select Fit</option>
                  <option value="regular">Regular Fit</option>
                  <option value="slim">Slim Fit</option>
                  <option value="oversized">Oversized</option>
                </select>
              </div>
            )}

            <input
              type="text"
              placeholder="Price"
              value={productForm.price}
              onChange={(e) =>
                setProductForm({ ...productForm, price: e.target.value })
              }
              required
              min="0"
              step="0.01"
            />

            <input
              type="number"
              placeholder="Stock Quantity"
              value={productForm.stock}
              onChange={(e) =>
                setProductForm({ ...productForm, stock: e.target.value })
              }
              required
              min="0"
            />

            <textarea
              placeholder="Description"
              value={productForm.description}
              onChange={(e) =>
                setProductForm({ ...productForm, description: e.target.value })
              }
              required
            />

            <div className="product-options">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={productForm.isMembersOnly}
                  onChange={(e) =>
                    setProductForm({
                      ...productForm,
                      isMembersOnly: e.target.checked,
                    })
                  }
                />
                Members Only
              </label>

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={productForm.isOutOfStock}
                  onChange={(e) =>
                    setProductForm({
                      ...productForm,
                      isOutOfStock: e.target.checked,
                    })
                  }
                />
                Out of Stock
              </label>
            </div>

            <div className="form-group">
              <label>External URL (Optional)</label>
              <input
                type="url"
                placeholder="https://example.com/product"
                value={productForm.externalUrl || ''}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    externalUrl: e.target.value,
                  })
                }
              />
              {productForm.externalUrl && (
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={productForm.isExternalProduct}
                    onChange={(e) =>
                      setProductForm({
                        ...productForm,
                        isExternalProduct: e.target.checked,
                      })
                    }
                  />
                  Send users directly to external site
                </label>
              )}
            </div>

            <button type="submit">
              {selectedProduct ? 'Update Product' : 'Add Product'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

ProductModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  productForm: PropTypes.shape({
    name: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    price: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    images: PropTypes.arrayOf(PropTypes.string).isRequired,
    variants: PropTypes.shape({
      colors: PropTypes.arrayOf(PropTypes.string).isRequired,
      sizes: PropTypes.arrayOf(PropTypes.string).isRequired,
    }).isRequired,
    details: PropTypes.shape({
      tshirt: PropTypes.shape({
        material: PropTypes.string,
        weight: PropTypes.string,
        fit: PropTypes.string,
      }).isRequired,
      mug: PropTypes.shape({
        capacity: PropTypes.string,
        material: PropTypes.string,
        dishwasherSafe: PropTypes.bool,
      }).isRequired,
      cap: PropTypes.shape({
        adjustable: PropTypes.bool,
        material: PropTypes.string,
      }).isRequired,
      keytag: PropTypes.shape({
        material: PropTypes.string,
        length: PropTypes.string,
      }).isRequired,
    }).isRequired,
    stock: PropTypes.string,
    sizes: PropTypes.arrayOf(PropTypes.string),
    isMembersOnly: PropTypes.bool.isRequired,
    isOutOfStock: PropTypes.bool.isRequired,
  }).isRequired,
  setProductForm: PropTypes.func.isRequired,
  selectedProduct: PropTypes.object,
};

const CartModal = ({ onClose, cart, setCart, onCheckout }) => {
  const { currentUser } = useAuth();
  const [customerDetails, setCustomerDetails] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
  });

  // Prefill user details when component mounts
  useEffect(() => {
    if (currentUser) {
      // Fetch user profile data from Firestore
      const fetchUserProfile = async () => {
        try {
          const docRef = doc(db, 'users', currentUser.uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const userData = docSnap.data();
            setCustomerDetails({
              name: userData.name || '',
              email: currentUser.email || '',
              phone: userData.phone || '',
              address: userData.address || '',
            });
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
        }
      };
      fetchUserProfile();
    }
  }, [currentUser]);

  const handleDetailsChange = (e) => {
    const { name, value } = e.target;
    setCustomerDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onCheckout(customerDetails);
  };

  const removeCartItem = (item) => {
    // Remove specific item based on id and index
    setCart(
      cart.filter(
        (cartItem, idx) =>
          !(
            cartItem.id === item.id &&
            cartItem.selectedVariants?.color === item.selectedVariants?.color &&
            cartItem.selectedVariants?.size === item.selectedVariants?.size
          ),
      ),
    );
  };

  if (!currentUser) {
    return (
      <div className="modal">
        <div className="modal-content">
          <button className="close-button" onClick={onClose}>
            <FaTimes />
          </button>
          <div className="auth-prompt">
            <h2>Sign in to Continue</h2>
            <p>
              Please sign in or create an account to proceed with your purchase.
            </p>
            <div className="auth-buttons">
              <Link to="/login" className="auth-button">
                Sign In
              </Link>
              <Link to="/register" className="auth-button register">
                Create Account
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <button className="close-button" onClick={onClose}>
            <FaTimes />
          </button>
          <h2>Shopping Cart</h2>

          {cart.length === 0 ? (
            <p>Your cart is empty</p>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="cart-items">
                {cart.map((item, index) => (
                  <div key={`${item.id}-${index}`} className="cart-item">
                    <div className="cart-item-details">
                      <h4>{item.name}</h4>
                      <div className="item-variants">
                        {item.selectedVariants?.color && (
                          <span className="variant">
                            Color: {item.selectedVariants.color}
                          </span>
                        )}
                        {item.selectedVariants?.size && (
                          <span className="variant">
                            Size: {item.selectedVariants.size}
                          </span>
                        )}
                        <span className="variant">
                          Qty: {item.quantity || 1}
                        </span>
                      </div>
                    </div>
                    <span className="item-price">LKR {item.price}</span>
                    <button
                      type="button"
                      className="remove-item"
                      onClick={() => removeCartItem(item)}
                    >
                      <FaTimes />
                    </button>
                  </div>
                ))}
                <div className="cart-total">
                  Total: LKR{' '}
                  {cart.reduce(
                    (sum, item) =>
                      sum + Number(item.price) * (item.quantity || 1),
                    0,
                  )}
                </div>
              </div>

              <div className="customer-details">
                <h3>Delivery Details</h3>
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={customerDetails.name}
                    onChange={handleDetailsChange}
                    required
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={customerDetails.email}
                    onChange={handleDetailsChange}
                    required
                    placeholder="Enter your email address"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Contact Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={customerDetails.phone}
                    onChange={handleDetailsChange}
                    required
                    placeholder="Enter your contact number"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="address">Delivery Address *</label>
                  <textarea
                    id="address"
                    name="address"
                    value={customerDetails.address}
                    onChange={handleDetailsChange}
                    required
                    placeholder="Enter your delivery address"
                    rows="3"
                  />
                </div>
              </div>

              <button
                type="submit"
                className="checkout-button"
                disabled={cart.length === 0}
              >
                Proceed to Checkout
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

CartModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  cart: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      price: PropTypes.number.isRequired,
    }),
  ).isRequired,
  setCart: PropTypes.func.isRequired,
  onCheckout: PropTypes.func.isRequired,
};

// Update the OrdersModal component
const OrdersModal = ({ orders = [], onClose }) => {
  const [selectedOrder, setSelectedOrder] = useState(null);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';

    // Check if it's a Firestore Timestamp
    if (timestamp?.toDate) {
      return timestamp.toDate().toLocaleString('en-US', {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
    }

    // Handle regular Date object or timestamp
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  // If an order is selected, show its details
  if (selectedOrder) {
    return (
      <div className="modal-overlay" onClick={() => setSelectedOrder(null)}>
        <div
          className="modal orders-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content">
            <button
              className="close-button back-button"
              onClick={() => setSelectedOrder(null)}
            >
              <FaChevronLeft />
            </button>
            <h2>Order Details</h2>

            <div className="order-info">
              <div className="order-header">
                <div>
                  <h3>Order #{selectedOrder.id.slice(-8)}</h3>
                  <p className="order-date">
                    {formatDate(selectedOrder.createdAt)}
                  </p>
                </div>
                <div className="order-status-badge">
                  <span className={`order-status ${selectedOrder.status}`}>
                    {selectedOrder.status}
                  </span>
                </div>
              </div>

              <div className="order-section">
                <h4>Items</h4>
                <div className="order-items">
                  {selectedOrder.items.map((item, index) => (
                    <div key={index} className="order-item">
                      <div className="item-info">
                        <h5>{item.name}</h5>
                        <div className="item-details">
                          {item.selectedVariants?.color && (
                            <span>Color: {item.selectedVariants.color}</span>
                          )}
                          {item.selectedVariants?.size && (
                            <span>Size: {item.selectedVariants.size}</span>
                          )}
                          <span>Qty: {item.quantity}</span>
                        </div>
                      </div>
                      <div className="item-price">
                        LKR {item.price * item.quantity}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="order-section">
                <h4>Delivery Details</h4>
                <div className="delivery-details">
                  <p>
                    <strong>Name:</strong> {selectedOrder.customerDetails.name}
                  </p>
                  <p>
                    <strong>Email:</strong>{' '}
                    {selectedOrder.customerDetails.email}
                  </p>
                  <p>
                    <strong>Phone:</strong>{' '}
                    {selectedOrder.customerDetails.phone}
                  </p>
                  <p>
                    <strong>Address:</strong>{' '}
                    {selectedOrder.customerDetails.address}
                  </p>
                </div>
              </div>

              <div className="order-summary">
                <div className="summary-row">
                  <span>Subtotal</span>
                  <span>LKR {selectedOrder.total}</span>
                </div>
                <div className="summary-row">
                  <span>Delivery</span>
                  <span>Free</span>
                </div>
                <div className="summary-row total">
                  <span>Total</span>
                  <span>LKR {selectedOrder.total}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main orders list view
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal orders-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <button className="close-button" onClick={onClose}>
            <FaTimes />
          </button>
          <h2>My Orders</h2>

          {orders.length === 0 ? (
            <p className="no-orders">No orders found</p>
          ) : (
            <div className="orders-list">
              {orders.map((order) => (
                <div
                  key={order.id}
                  className="order-card"
                  onClick={() => setSelectedOrder(order)}
                >
                  <div className="order-card-header">
                    <h3>Order #{order.id.slice(-8)}</h3>
                    <span className={`order-status ${order.status}`}>
                      {order.status}
                    </span>
                  </div>
                  <div className="order-card-content">
                    <div className="order-summary-brief">
                      <p>{order.items.length} item(s)</p>
                      <p>LKR {order.total}</p>
                    </div>
                    <p className="order-date">{formatDate(order.createdAt)}</p>
                  </div>
                  <button className="view-details-btn">
                    View Details <FaChevronRight />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Update PropTypes to include all required fields
OrdersModal.propTypes = {
  orders: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          quantity: PropTypes.number.isRequired,
          price: PropTypes.number.isRequired,
          selectedVariants: PropTypes.shape({
            color: PropTypes.string,
            size: PropTypes.string,
          }),
        }),
      ).isRequired,
      total: PropTypes.number.isRequired,
      createdAt: PropTypes.object,
      customerDetails: PropTypes.shape({
        name: PropTypes.string.isRequired,
        email: PropTypes.string.isRequired,
        phone: PropTypes.string.isRequired,
        address: PropTypes.string.isRequired,
      }),
    }),
  ),
  onClose: PropTypes.func.isRequired,
};

// Update ImageSliderModal component
const ImageSliderModal = ({ product, images, onClose, onAddToCart }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedVariants, setSelectedVariants] = useState({
    color: '',
    size: '',
    quantity: 1,
  });

  const handlePrev = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleAddToCart = () => {
    if (
      (!product.variants?.colors?.length || selectedVariants.color) &&
      (!product.sizes?.length || selectedVariants.size)
    ) {
      onAddToCart([
        {
          ...product,
          selectedVariants: {
            color: selectedVariants.color,
            size: selectedVariants.size,
          },
          quantity: selectedVariants.quantity,
        },
      ]);
      onClose();
    } else {
      alert('Please select all required options');
    }
  };

  return (
    <div className="modal product-modal" onClick={onClose}>
      <div
        className="modal-content product-view"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="close-button" onClick={onClose}>
          <FaTimes />
        </button>

        <div className="product-view-container">
          {/* Left side - Image slider */}
          <div className="product-images-section">
            <div className="image-slider">
              <button className="nav-button prev" onClick={handlePrev}>
                <FaChevronLeft />
              </button>
              <img
                src={images[currentIndex]}
                alt={`Slide ${currentIndex + 1}`}
              />
              <button className="nav-button next" onClick={handleNext}>
                <FaChevronRight />
              </button>
            </div>
            <div className="image-thumbnails">
              {images.map((img, index) => (
                <img
                  key={index}
                  src={img}
                  alt={`Thumbnail ${index + 1}`}
                  className={currentIndex === index ? 'active' : ''}
                  onClick={() => setCurrentIndex(index)}
                />
              ))}
            </div>
          </div>

          {/* Right side - Product details */}
          <div className="product-details-section">
            <h2>{product.name}</h2>
            <div className="product-price">LKR {product.price}</div>

            <div className="product-description">
              <h3>Description</h3>
              <p>{product.description}</p>
            </div>

            <div className="product-variations">
              {product.variants?.colors?.length > 0 && (
                <div className="variation-section">
                  <h3>Available Colors</h3>
                  <div className="variation-options">
                    {product.variants.colors.map((color) => (
                      <div
                        key={color}
                        className={`variation-option ${
                          selectedVariants.color === color ? 'selected' : ''
                        }`}
                        onClick={() =>
                          setSelectedVariants((prev) => ({ ...prev, color }))
                        }
                      >
                        <span
                          className="color-swatch"
                          style={{
                            backgroundColor: color.toLowerCase(),
                            border:
                              color.toLowerCase() === 'white'
                                ? '1px solid #ddd'
                                : 'none',
                          }}
                        />
                        <span className="variation-label">{color}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {product.sizes?.length > 0 && (
                <div className="variation-section">
                  <h3>Available Sizes</h3>
                  <div className="variation-options">
                    {product.sizes.map((size) => (
                      <div
                        key={size}
                        className={`variation-option size-chip ${
                          selectedVariants.size === size ? 'selected' : ''
                        }`}
                        onClick={() =>
                          setSelectedVariants((prev) => ({ ...prev, size }))
                        }
                      >
                        {size}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="quantity-selector">
                <h3>Quantity</h3>
                <div className="quantity-controls">
                  <button
                    onClick={() =>
                      setSelectedVariants((prev) => ({
                        ...prev,
                        quantity: Math.max(1, prev.quantity - 1),
                      }))
                    }
                  >
                    -
                  </button>
                  <input
                    type="number"
                    min="1"
                    value={selectedVariants.quantity}
                    onChange={(e) =>
                      setSelectedVariants((prev) => ({
                        ...prev,
                        quantity: parseInt(e.target.value) || 1,
                      }))
                    }
                  />
                  <button
                    onClick={() =>
                      setSelectedVariants((prev) => ({
                        ...prev,
                        quantity: prev.quantity + 1,
                      }))
                    }
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            {product.category === 'tshirt' && (
              <div className="specifications">
                <h3>Specifications</h3>
                <ul>
                  <li>
                    <strong>Material:</strong>{' '}
                    {product.details?.tshirt?.material}
                  </li>
                  <li>
                    <strong>Weight:</strong> {product.details?.tshirt?.weight}
                  </li>
                  <li>
                    <strong>Fit:</strong> {product.details?.tshirt?.fit}
                  </li>
                </ul>
              </div>
            )}

            <button
              className="add-to-cart-btn"
              onClick={handleAddToCart}
              disabled={
                (product.variants?.colors?.length && !selectedVariants.color) ||
                (product.sizes?.length && !selectedVariants.size)
              }
            >
              Add to Cart
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Update PropTypes
ImageSliderModal.propTypes = {
  product: PropTypes.shape({
    name: PropTypes.string.isRequired,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    description: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    details: PropTypes.object,
    variants: PropTypes.shape({
      colors: PropTypes.arrayOf(PropTypes.string),
    }),
    sizes: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  images: PropTypes.arrayOf(PropTypes.string).isRequired,
  onClose: PropTypes.func.isRequired,
  onAddToCart: PropTypes.func.isRequired,
};

// Update AddToCartModal component
const AddToCartModal = ({ product, onClose, onAdd }) => {
  const [selectedVariant, setSelectedVariant] = useState({
    color: '',
    size: '',
    quantity: 1,
  });

  // Get available sizes based on product category
  const getAvailableSizes = () => {
    if (product.category === 'tshirt') {
      return product.sizes || [];
    }
    return product.variants.sizes || [];
  };

  const isValid =
    (!product.variants.colors.length || selectedVariant.color) &&
    (!getAvailableSizes().length || selectedVariant.size) &&
    selectedVariant.quantity > 0;

  return (
    <div className="modal">
      <div className="modal-content">
        <button className="close-button" onClick={onClose}>
          <FaTimes />
        </button>
        <h2>Add to Cart - {product.name}</h2>

        <div className="variant-selection">
          {/* Size Selection */}
          {getAvailableSizes().length > 0 && (
            <div className="variant-field">
              <label>Size:</label>
              <select
                value={selectedVariant.size}
                onChange={(e) =>
                  setSelectedVariant({
                    ...selectedVariant,
                    size: e.target.value,
                  })
                }
                required
              >
                <option value="">Select Size</option>
                {getAvailableSizes().map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Color Selection */}
          {product.variants.colors.length > 0 && (
            <div className="variant-field">
              <label>Color:</label>
              <select
                value={selectedVariant.color}
                onChange={(e) =>
                  setSelectedVariant({
                    ...selectedVariant,
                    color: e.target.value,
                  })
                }
                required
              >
                <option value="">Select Color</option>
                {product.variants.colors.map((color) => (
                  <option key={color} value={color}>
                    {color}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Quantity Selection */}
          <div className="variant-field">
            <label>Quantity:</label>
            <input
              type="number"
              min="1"
              value={selectedVariant.quantity}
              onChange={(e) =>
                setSelectedVariant({
                  ...selectedVariant,
                  quantity: parseInt(e.target.value) || 1,
                })
              }
              required
            />
          </div>
        </div>

        {/* Show selected options summary */}
        <div className="selection-summary">
          <p>Selected Options:</p>
          <ul>
            {selectedVariant.size && <li>Size: {selectedVariant.size}</li>}
            {selectedVariant.color && <li>Color: {selectedVariant.color}</li>}
            <li>Quantity: {selectedVariant.quantity}</li>
          </ul>
        </div>

        <div className="modal-actions">
          <button
            onClick={() =>
              onAdd([
                {
                  ...product,
                  selectedVariants: {
                    color: selectedVariant.color,
                    size: selectedVariant.size,
                  },
                  quantity: selectedVariant.quantity,
                },
              ])
            }
            disabled={!isValid}
            className="add-to-cart-button"
          >
            Add to Cart
          </button>
        </div>
      </div>
    </div>
  );
};

// Update AddToCartModal PropTypes
AddToCartModal.propTypes = {
  product: PropTypes.shape({
    name: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    variants: PropTypes.shape({
      colors: PropTypes.arrayOf(PropTypes.string),
      sizes: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
    sizes: PropTypes.arrayOf(PropTypes.string), // For t-shirt specific sizes
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
};

// Add this component near the top with other modals
const AddToCartConfirmationModal = ({
  product,
  quantity,
  onClose,
  onConfirm,
}) => {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal confirmation-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          <button className="close-button" onClick={onClose}>
            <FaTimes />
          </button>
          <h2>Add to Cart - {product.name}</h2>

          <div className="confirmation-details">
            <div className="quantity-section">
              <label>Quantity:</label>
              <input type="number" value={quantity} min="1" readOnly />
            </div>

            <div className="selected-options">
              <h3>Selected Options:</h3>
              <p>Quantity: {quantity}</p>
            </div>
          </div>

          <div className="modal-footer">
            <button className="btn-submit" onClick={onConfirm}>
              Add to Cart
            </button>
            <button className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Add this component near other modals
const OrderConfirmationModal = ({ onClose }) => {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal confirmation-modal success"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          <h2>Order Placed Successfully! 🎉</h2>
          <div className="confirmation-message">
            <p>
              Thank you for your order. We have received your order and will
              process it shortly.
            </p>
            <p>
              You will receive a confirmation email with your order details.
            </p>
          </div>
          <button className="btn-submit" onClick={onClose}>
            Continue Shopping
          </button>
        </div>
      </div>
    </div>
  );
};

// Add this component near other modals
const SignInPromptModal = ({ onClose }) => {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal signin-prompt-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          <button className="close-button" onClick={onClose}>
            <FaTimes />
          </button>
          <h2>Sign In Required</h2>
          <div className="signin-prompt-content">
            <p>
              Please sign in or create an account to view product details and
              make purchases.
            </p>
            <div className="auth-buttons">
              <Link to="/login" className="btn-submit">
                Sign In
              </Link>
              <Link to="/register" className="btn-secondary">
                Create Account
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Add this component for fullscreen image
const FullscreenImage = ({ src, onClose }) => {
  return (
    <div className="fullscreen-image-modal" onClick={onClose}>
      <button className="fullscreen-close" onClick={onClose}>
        <FaTimes />
      </button>
      <img
        src={src}
        alt="Fullscreen view"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
};

// Update the ProductDetailsModal component
const ProductDetailsModal = ({ product, onClose, onAddToCart }) => {
  const { currentUser } = useAuth();
  const [showSignInPrompt, setShowSignInPrompt] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Helper function to determine button text and state
  const getButtonState = () => {
    if (product.isOutOfStock) {
      return {
        text: 'Out of Stock',
        disabled: true,
        className: 'out-of-stock',
      };
    }
    if (product.isMembersOnly && !currentUser) {
      return {
        text: 'Members Only',
        disabled: false, // Not disabled so we can show sign in prompt
        className: 'members-only',
      };
    }
    return {
      text: 'Add to Cart',
      disabled: false,
      className: '',
    };
  };

  const handleActionClick = () => {
    if (product.isExternalProduct && product.externalUrl) {
      window.open(product.externalUrl, '_blank');
    } else {
      onAddToCart(product);
    }
  };

  const buttonState = getButtonState();

  const handleImageClick = (imageUrl) => {
    setFullscreenImage(imageUrl);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal product-details-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          <button className="close-button" onClick={onClose}>
            <FaTimes />
          </button>

          <div className="product-details">
            <div className="product-images-slider">
              <img
                src={product.images?.[currentImageIndex]}
                alt={`${product.name} - View ${currentImageIndex + 1}`}
                onClick={() =>
                  handleImageClick(product.images?.[currentImageIndex])
                }
              />
              {product.images?.length > 1 && (
                <div className="image-navigation">
                  <button
                    onClick={() =>
                      setCurrentImageIndex((prev) =>
                        prev === 0 ? product.images.length - 1 : prev - 1,
                      )
                    }
                  >
                    <FaChevronLeft />
                  </button>
                  <button
                    onClick={() =>
                      setCurrentImageIndex((prev) =>
                        prev === product.images.length - 1 ? 0 : prev + 1,
                      )
                    }
                  >
                    <FaChevronRight />
                  </button>
                </div>
              )}
            </div>

            <div className="product-info">
              <h2>{product.name}</h2>
              {product.isMembersOnly && (
                <div className="members-only-badge">
                  <FaStar /> Members Only
                </div>
              )}
              <div className="price">LKR {product.price}</div>
              <p className="description">{product.description}</p>

              {product.externalUrl && (
                <a
                  href={product.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="product-url"
                >
                  {product.externalUrl}
                </a>
              )}

              <div className="product-actions">
                <button
                  className={`btn-add-to-cart ${buttonState.className}`}
                  onClick={handleActionClick}
                  disabled={buttonState.disabled}
                >
                  {product.isExternalProduct
                    ? 'Visit Merchant'
                    : buttonState.text}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {fullscreenImage && (
        <FullscreenImage
          src={fullscreenImage}
          onClose={() => setFullscreenImage(null)}
        />
      )}

      {showSignInPrompt && (
        <SignInPromptModal onClose={() => setShowSignInPrompt(false)} />
      )}
    </div>
  );
};

function Store() {
  const { currentUser } = useAuth();
  const [products, setProducts] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCartModal, setShowCartModal] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [cart, setCart] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [orders, setOrders] = useState([]);
  const [productForm, setProductForm] = useState({
    name: '',
    category: '',
    price: '',
    description: '',
    images: [''],
    variants: {
      colors: [],
      sizes: [],
    },
    details: {
      tshirt: {
        material: '',
        weight: '',
        fit: '',
      },
      mug: {
        capacity: '',
        material: '',
        dishwasherSafe: false,
      },
      cap: {
        adjustable: false,
        material: '',
      },
      keytag: {
        material: '',
        length: '',
      },
    },
    stock: '0',
    externalUrl: '',
    isMembersOnly: false,
    isExternalProduct: false,
  });
  const [showImageSlider, setShowImageSlider] = useState(false);
  const [showVariationPicker, setShowVariationPicker] = useState(false);
  const [selectedProductForModal, setSelectedProductForModal] = useState(null);
  const [showAddToCartConfirmation, setShowAddToCartConfirmation] =
    useState(false);
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [selectedProductForCart, setSelectedProductForCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOrderConfirmation, setShowOrderConfirmation] = useState(false);

  // Fetch products once on mount
  useEffect(() => {
    fetchProducts();
  }, []);

  // Fetch orders when user changes
  useEffect(() => {
    if (currentUser) {
      fetchOrders();
    } else {
      setOrders([]); // Clear orders when user logs out
    }
  }, [currentUser]);

  // Update handleProductSubmit to use the resetForm
  const handleProductSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedProduct) {
        await updateDoc(doc(db, 'products', selectedProduct.id), productForm);
      } else {
        await addDoc(collection(db, 'products'), productForm);
      }
      setShowAddModal(false);
      setSelectedProduct(null);
      setProductForm({
        name: '',
        category: '',
        price: '',
        description: '',
        images: [''],
        variants: {
          colors: [],
          sizes: [],
        },
        details: {
          tshirt: {
            material: '',
            weight: '',
            fit: '',
          },
          mug: {
            capacity: '',
            material: '',
            dishwasherSafe: false,
          },
          cap: {
            adjustable: false,
            material: '',
          },
          keytag: {
            material: '',
            length: '',
          },
        },
        stock: '0',
        externalUrl: '',
        isMembersOnly: false,
        isExternalProduct: false,
      });
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
    }
  };

  // Update setSelectedProduct to properly initialize form
  useEffect(() => {
    if (selectedProduct) {
      setProductForm({
        ...productForm,
        ...selectedProduct,
        variants: {
          ...productForm.variants,
          ...selectedProduct.variants,
        },
        details: {
          ...productForm.details,
          ...selectedProduct.details,
        },
      });
    } else {
      setProductForm(productForm);
    }
  }, [selectedProduct]);

  const fetchProducts = async () => {
    try {
      const productsRef = collection(db, 'products');
      const snapshot = await getDocs(productsRef);

      const productsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Sort products: active first, then out of stock
      const sortedProducts = productsData.sort((a, b) => {
        // If one is out of stock and the other isn't, sort accordingly
        if (a.isOutOfStock !== b.isOutOfStock) {
          return a.isOutOfStock ? 1 : -1;
        }

        // If both have same stock status, sort by creation date (newest first)
        const dateA = a.createdAt?.toDate() || new Date(0);
        const dateB = b.createdAt?.toDate() || new Date(0);
        return dateB - dateA;
      });

      setProducts(sortedProducts);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      if (!currentUser) return; // Don't fetch if no user

      // Create query to get only user's orders, ordered by creation date
      const ordersRef = collection(db, 'orders');
      const q = query(
        ordersRef,
        where('userId', '==', currentUser.uid),
        orderBy('createdAt', 'desc'),
      );

      const snapshot = await getDocs(q);
      const ordersData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        // Ensure createdAt is properly formatted
        createdAt: doc.data().createdAt?.toDate() || new Date(),
      }));

      setOrders(ordersData);
    } catch (error) {
      console.error('Error fetching orders:', error);
      // Optionally show error to user
      toast.error('Failed to load orders. Please try again.');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?'))
      return;
    try {
      await deleteDoc(doc(db, 'products', productId));
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  };

  const handleCheckout = async (customerDetails) => {
    if (!currentUser) {
      alert('Please login to checkout');
      return;
    }

    try {
      const orderData = {
        userId: currentUser.uid,
        items: cart.map((item) => ({
          id: item.id,
          name: item.name,
          price: item.price,
          category: item.category,
          selectedVariants: item.selectedVariants,
          quantity: item.quantity || 1,
          details: item.details || {},
        })),
        total: cart.reduce(
          (sum, item) => sum + Number(item.price) * (item.quantity || 1),
          0,
        ),
        status: 'pending',
        createdAt: new Date(),
        customerDetails: customerDetails,
      };

      await addDoc(collection(db, 'orders'), orderData);
      setCart([]);
      setShowCartModal(false);
      setShowOrderConfirmation(true);
    } catch (error) {
      console.error('Error creating order:', error);
      alert('Failed to place order. Please try again.');
    }
  };

  // Handler for opening image slider
  const handleImageSliderOpen = (product) => {
    setSelectedProductForModal(product);
    setShowImageSlider(true);
  };

  // Handler for opening variation picker
  const handleVariationPickerOpen = (product) => {
    setSelectedProductForModal(product);
    setShowVariationPicker(true);
  };

  // In the Store component, add this new handler
  const handleDirectAddToCart = (items) => {
    setCart([...cart, ...items]);
    setShowImageSlider(false);
    setSelectedProductForModal(null);
  };

  // Update handleVariationPickerOpen
  const handleAddToCart = (product) => {
    setSelectedProductForCart(product);
    setSelectedQuantity(1);
    setShowAddToCartConfirmation(true);
  };

  // Add confirmation handler
  const handleConfirmAddToCart = () => {
    setCart([
      ...cart,
      {
        ...selectedProductForCart,
        quantity: selectedQuantity,
        selectedVariants: selectedProductForCart.selectedVariants || {},
      },
    ]);
    setShowAddToCartConfirmation(false);
    setSelectedProductForCart(null);
  };

  return (
    <div className="store-page">
      <section className="hero section">
        <div className="container">
          <h1 className="section-title">Store</h1>
          <p className="subtitle">
            Explore our collection of products and member-exclusive items
          </p>
        </div>
      </section>

      <div className="store-content container">
        <div className="store-actions">
          {currentUser?.isAdmin && (
            <button onClick={() => setShowAddModal(true)}>
              <FaPlus /> Add Product
            </button>
          )}
          {currentUser && (
            <>
              <button onClick={() => setShowCartModal(true)}>
                <FaShoppingCart /> Cart ({cart.length})
              </button>
              <button onClick={() => setShowOrderModal(true)}>
                <FaBoxOpen /> My Orders
              </button>
            </>
          )}
        </div>

        <div className="products-grid">
          {products.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              onImageClick={handleImageSliderOpen}
              onAddToCartClick={handleAddToCart}
              isAdmin={currentUser?.isAdmin}
              onEdit={() => {
                setSelectedProduct(product);
                setShowAddModal(true);
              }}
              onDelete={() => handleDeleteProduct(product.id)}
            />
          ))}
        </div>
      </div>

      {/* Modals */}
      {showCartModal && (
        <CartModal
          onClose={() => setShowCartModal(false)}
          cart={cart}
          setCart={setCart}
          onCheckout={handleCheckout}
        />
      )}

      {showAddModal && (
        <ProductModal
          onClose={() => setShowAddModal(false)}
          onSubmit={handleProductSubmit}
          productForm={productForm}
          setProductForm={setProductForm}
          selectedProduct={selectedProduct}
        />
      )}

      {showOrderModal && (
        <OrdersModal onClose={() => setShowOrderModal(false)} orders={orders} />
      )}

      {showImageSlider && selectedProductForModal && (
        <ImageSliderModal
          product={selectedProductForModal}
          images={selectedProductForModal.images}
          onClose={() => {
            setShowImageSlider(false);
            setSelectedProductForModal(null);
          }}
          onAddToCart={handleDirectAddToCart}
        />
      )}

      {showVariationPicker && selectedProductForModal && (
        <AddToCartModal
          product={selectedProductForModal}
          onClose={() => {
            setShowVariationPicker(false);
            setSelectedProductForModal(null);
          }}
          onAdd={(items) => {
            setCart([...cart, ...items]);
            setShowVariationPicker(false);
            setSelectedProductForModal(null);
          }}
        />
      )}

      {/* Add the confirmation modal */}
      {showAddToCartConfirmation && selectedProductForCart && (
        <AddToCartConfirmationModal
          product={selectedProductForCart}
          quantity={selectedQuantity}
          onClose={() => {
            setShowAddToCartConfirmation(false);
            setSelectedProductForCart(null);
          }}
          onConfirm={handleConfirmAddToCart}
        />
      )}

      {showOrderConfirmation && (
        <OrderConfirmationModal
          onClose={() => setShowOrderConfirmation(false)}
        />
      )}
    </div>
  );
}

export default Store;
